// TODO: remove with https://github.com/combinaut/sparkle/issues/32203 once embed editors use sp apps.
import { useEventListener } from "@vueuse/core";
import { settleMount } from "#ce/framework/mount-ready";
import { serializeFormFields } from "#/admin/legacy/forms/serialize-form-fields.js";
import { createEmbedPreviewApi } from "#/admin/legacy/forms/api/embed-preview.js";

const initialized = new WeakSet();

/**
 * Initializes live sandbox previews from the form's server-rendered endpoint and configuration, once the form's
 * sp-selects have mounted or failed to.
 */
export function initializeEmbedPreviews(root) {
  const forms = [...(root?.querySelectorAll('[data-behaviour~="embed-preview"]') || [])];

  if (root?.matches?.('[data-behaviour~="embed-preview"]')) {
    forms.unshift(root);
  }

  return Promise.all(forms.map(initializeEmbedPreview));
}

async function initializeEmbedPreview(form) {
  if (initialized.has(form)) {
    return;
  }

  initialized.add(form);

  await Promise.all([...form.querySelectorAll("sp-select")].map(settleMount));

  if (!form.isConnected) {
    initialized.delete(form);

    return;
  }

  const { previewPrefix, previewEndpoint, previewOrigin, previewContainer, previewIframeId } = form.dataset;
  const prefixes = JSON.parse(form.dataset.previewAttributes).map((attribute) => `${previewPrefix}[${attribute}]`);
  const container = document.querySelector(previewContainer);

  if (!container) {
    return;
  }

  const previewApi = createEmbedPreviewApi();

  let cancelRender;
  let iframe;

  function renderPreview() {
    cancelRender?.();
    iframe?.remove();
    iframe = document.createElement("iframe");
    iframe.id = previewIframeId;

    const currentIframe = iframe;
    const abortController = new AbortController();
    let resolveReady;
    const ready = new Promise((resolve) => (resolveReady = resolve));

    function handlePreviewMessage(event) {
      if (
        event.origin === previewOrigin &&
        event.source === currentIframe.contentWindow &&
        event.data?.status === "ready-for-content"
      ) {
        resolveReady(true);
        stopListening();
      }
    }

    const stopListening = useEventListener(window, "message", handlePreviewMessage);

    function cancelPreview() {
      stopListening();
      resolveReady(false);
      abortController.abort();
    }

    cancelRender = cancelPreview;
    iframe.src = previewEndpoint;
    container.append(iframe);

    const body = new URLSearchParams(serializeFormFields(form, prefixes));
    const content = previewApi.render(previewEndpoint, body, { signal: abortController.signal });

    function replacePreviewContent([isReady, html]) {
      if (isReady && currentIframe === iframe && form.isConnected) {
        const message = {
          action: "replace",
          replaceHTML: html,
        };

        currentIframe.contentWindow.postMessage(message, previewOrigin);
      }
    }

    function handlePreviewFailure() {
      cancelPreview();
    }

    Promise.all([ready, content]).then(replacePreviewContent).catch(handlePreviewFailure);
  }

  function handlePreviewChange(event) {
    const control = event.target;

    if (
      control instanceof HTMLElement &&
      control.name?.startsWith(`${previewPrefix}[`) &&
      control.dataset.refreshPreview !== "false"
    ) {
      renderPreview();
    }
  }

  useEventListener(form, "change", handlePreviewChange);
  renderPreview();
}
